'use client';

/**
 * AnimatedSwitch — Morfi's switch primitive.
 *
 * Implementation transcribed from the Apple Switch reference at
 * https://ui.unlumen.com/components/apple-switch (registry payload at
 * https://ui.unlumen.com/r/apple-switch.json), with all colors and dimensions
 * replaced by Morfi semantic tokens. The Apple physics constants
 * (THUMB_SPRING, GRAB_SPRING) are preserved verbatim — the tactile feel is
 * the upstream contribution; Morfi owns the visual identity via VARIANT_TOKENS
 * + Tailwind utilities.
 *
 * This file is NOT part of the shadcn registry and is NOT regenerated by
 * `pnpm refresh:shadcn-stock`. No `.shadcn-stock/switch.tsx` snapshot exists;
 * FQ-111 stays silent. See spec 054 (frontend/specs/054-apple-switch).
 */
import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useMotionValue, useReducedMotion, useSpring, useTransform } from 'motion/react';
import type * as React from 'react';
import { type ButtonHTMLAttributes, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type Ref, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/ui/utils';
export type AnimatedSwitchVariant = 'brand' | 'success' | 'destructive';
export type AnimatedSwitchSize = 'default' | 'sm';
export interface AnimatedSwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'role' | 'children' | 'value' | 'defaultValue'> {
  variant?: AnimatedSwitchVariant;
  size?: AnimatedSwitchSize;
  /**
   * Tailwind classes for the off-state track background. Replaces the default
   * `bg-border-primary`. Use when the default visually merges with the
   * surrounding surface (e.g. inside a Panel, where --border-secondary and
   * --background-primary resolve to similar colors in dark mode). Pass any
   * Tailwind background utility, e.g. `uncheckedClassName="bg-foreground-primary/10"`.
   */
  uncheckedClassName?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (next: boolean) => void;
}

// ──────────────────────────────────────────────────────────────────────────
// Module constants — variant + size resolution
// ──────────────────────────────────────────────────────────────────────────

interface SwitchMetrics {
  trackX: number;
  trackY: number;
  thumbX: number;
  thumbY: number;
  padding: number;
  thumbTravel: number;
  trackClass: string;
  thumbClass: string;
}

// Morfi compact dimensions — see spec 054 §R-6. Smaller than upstream Apple
// (62×30 / 46×24) because most call-sites are dense settings panels.
//
// thumbTravel = trackX − thumbX − 2×padding. TRACK_BASE must not add a horizontal
// border (border-box would shrink the track inner width vs these pixel constants).
const SWITCH_METRICS: Record<AnimatedSwitchSize, SwitchMetrics> = {
  default: {
    trackX: 40,
    trackY: 20,
    thumbX: 20,
    thumbY: 16,
    padding: 2,
    thumbTravel: 16,
    trackClass: 'h-5 w-10',
    thumbClass: 'h-4 w-5'
  },
  sm: {
    trackX: 28,
    trackY: 14,
    thumbX: 12,
    thumbY: 10,
    padding: 2,
    thumbTravel: 12,
    trackClass: 'h-3.5 w-7',
    thumbClass: 'h-2.5 w-3'
  }
};
const VARIANT_TRACK_ON_CLASS: Record<AnimatedSwitchVariant, string> = {
  brand: 'bg-button-primary-bg',
  success: 'bg-feedback-success-accent',
  destructive: 'bg-feedback-error-accent'
};
const VARIANT_RING_CLASS: Record<AnimatedSwitchVariant, string> = {
  brand: 'focus-visible:ring-button-primary-ring/30',
  success: 'focus-visible:ring-feedback-success-border/30',
  destructive: 'focus-visible:ring-feedback-error-border/30'
};
interface VariantTokenSet {
  onCss: string;
  glowCss: string;
}

// CSS-variable strings for the inline `style.backgroundColor` on motion-driven
// layers (glow halo + on-track-fill overlay). Going inline is required because
// Tailwind has no built-in utility for color-mix(in srgb, var(--token) X%, transparent)
// with a dynamic token name. See spec 054 §R-13.
const VARIANT_TOKENS: Record<AnimatedSwitchVariant, VariantTokenSet> = {
  brand: {
    onCss: 'var(--button-primary-bg)',
    glowCss: 'color-mix(in srgb, var(--button-primary-bg) 32%, transparent)'
  },
  success: {
    onCss: 'var(--feedback-success-accent)',
    glowCss: 'color-mix(in srgb, var(--feedback-success-accent) 32%, transparent)'
  },
  destructive: {
    onCss: 'var(--feedback-error-accent)',
    glowCss: 'color-mix(in srgb, var(--feedback-error-accent) 32%, transparent)'
  }
};

// Spring constants preserved verbatim from upstream Apple Switch.
const THUMB_SPRING = {
  stiffness: 700,
  damping: 48,
  mass: 0.55
} as const;
const GRAB_SPRING = {
  stiffness: 500,
  damping: 25
} as const;
const TRACK_BASE: string = 'group/switch peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full ' + 'border-0 shadow-elevation-inset outline-none ' + 'focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 ' + 'active:cursor-grabbing';
const TRACK_OFF: string = 'bg-border-primary';
const THUMB_BASE: string = 'pointer-events-none block rounded-full bg-background-primary dark:bg-foreground-primary ring-0';
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// Apple "glass" optical-detail shadows. Built with color-mix + CSS color
// keywords so FQ-004 (which scans .tsx files for hex / rgb / hsl / oklch
// literals) stays silent — these are optical highlights, not semantic colors.
// They convey "this is a glass surface" regardless of variant or theme.
// See spec 054 §R-7.
const TRACK_INSET_SHADOW: string = 'inset 0 1px 1px color-mix(in srgb, white 34%, transparent), ' + 'inset 0 -1px 2px color-mix(in srgb, black 8%, transparent)';
const THUMB_GLASS_SHADOW: string = '0 3px 11px color-mix(in srgb, black 24%, transparent), ' + '0 1px 1px color-mix(in srgb, black 12%, transparent), ' + 'inset 0 1px 0 color-mix(in srgb, white 78%, transparent), ' + 'inset 0 -1px 1px color-mix(in srgb, black 5%, transparent)';

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

export function AnimatedSwitch(t0) {
  const $ = _c(90);
  let checked;
  let className;
  let defaultChecked;
  let disabled;
  let onCheckedChange;
  let onClick;
  let onPointerCancel;
  let onPointerDown;
  let onPointerLeave;
  let onPointerMove;
  let onPointerUp;
  let ref;
  let rest;
  let t1;
  let t2;
  let t3;
  let uncheckedClassName;
  if ($[0] !== t0) {
    ({
      variant: t1,
      size: t2,
      className,
      uncheckedClassName,
      checked,
      defaultChecked,
      onCheckedChange,
      disabled,
      onClick,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      type: t3,
      ref,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = className;
    $[3] = defaultChecked;
    $[4] = disabled;
    $[5] = onCheckedChange;
    $[6] = onClick;
    $[7] = onPointerCancel;
    $[8] = onPointerDown;
    $[9] = onPointerLeave;
    $[10] = onPointerMove;
    $[11] = onPointerUp;
    $[12] = ref;
    $[13] = rest;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = uncheckedClassName;
  } else {
    checked = $[1];
    className = $[2];
    defaultChecked = $[3];
    disabled = $[4];
    onCheckedChange = $[5];
    onClick = $[6];
    onPointerCancel = $[7];
    onPointerDown = $[8];
    onPointerLeave = $[9];
    onPointerMove = $[10];
    onPointerUp = $[11];
    ref = $[12];
    rest = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    uncheckedClassName = $[17];
  }
  const variant = t1 === undefined ? "brand" : t1;
  const size = t2 === undefined ? "default" : t2;
  const type = t3 === undefined ? "button" : t3;
  const prefersReduced = useReducedMotion();
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked ?? false);
  const isControlled = checked !== undefined;
  const currentChecked = isControlled ? checked : uncontrolledChecked;
  const metrics = SWITCH_METRICS[size];
  const offClass = uncheckedClassName ?? TRACK_OFF;
  let T0;
  let dataState;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let thumbClassName;
  if ($[18] !== className || $[19] !== currentChecked || $[20] !== disabled || $[21] !== isControlled || $[22] !== metrics || $[23] !== offClass || $[24] !== onCheckedChange || $[25] !== onClick || $[26] !== onPointerCancel || $[27] !== onPointerDown || $[28] !== onPointerLeave || $[29] !== onPointerMove || $[30] !== onPointerUp || $[31] !== prefersReduced || $[32] !== ref || $[33] !== rest || $[34] !== size || $[35] !== type || $[36] !== variant) {
    t21 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const trackClassName = cn(TRACK_BASE, metrics.trackClass, currentChecked ? VARIANT_TRACK_ON_CLASS[variant] : offClass, VARIANT_RING_CLASS[variant], className);
      thumbClassName = cn(THUMB_BASE, metrics.thumbClass);
      dataState = currentChecked ? "checked" : "unchecked";
      if (prefersReduced === true) {
        const reducedThumbClassName = cn(thumbClassName, "absolute left-[2px] top-1/2 z-2 -translate-y-1/2", size === "default" && "data-[state=checked]:translate-x-[12px] data-[state=unchecked]:translate-x-0", size === "sm" && "data-[state=checked]:translate-x-[8px] data-[state=unchecked]:translate-x-0");
        let t22;
        if ($[58] !== currentChecked || $[59] !== disabled || $[60] !== isControlled || $[61] !== onCheckedChange || $[62] !== onClick) {
          t22 = function handleReducedClick(event) {
            onClick?.(event);
            if (event.defaultPrevented || disabled === true) {
              return;
            }
            const next = !currentChecked;
            if (!isControlled) {
              setUncontrolledChecked(next);
            }
            onCheckedChange?.(next);
          };
          $[58] = currentChecked;
          $[59] = disabled;
          $[60] = isControlled;
          $[61] = onCheckedChange;
          $[62] = onClick;
          $[63] = t22;
        } else {
          t22 = $[63];
        }
        const handleReducedClick = t22;
        let t23;
        if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
          t23 = {
            touchAction: "pan-y"
          };
          $[64] = t23;
        } else {
          t23 = $[64];
        }
        let t24;
        if ($[65] !== dataState || $[66] !== reducedThumbClassName) {
          t24 = <span data-slot="switch-thumb" data-state={dataState} className={reducedThumbClassName} />;
          $[65] = dataState;
          $[66] = reducedThumbClassName;
          $[67] = t24;
        } else {
          t24 = $[67];
        }
        t21 = <button ref={ref} type={type} role="switch" aria-checked={currentChecked} data-slot="switch" data-state={dataState} data-variant={variant} data-size={size} disabled={disabled} onClick={handleReducedClick} className={trackClassName} style={t23} {...rest}>{t24}</button>;
        break bb0;
      }
      T0 = NormalMotionSwitch;
      t4 = ref ?? null;
      t5 = type;
      t6 = variant;
      t7 = size;
      t8 = metrics;
      t9 = currentChecked;
      t10 = isControlled;
      t11 = setUncontrolledChecked;
      t12 = onCheckedChange;
      t13 = disabled;
      t14 = onClick;
      t15 = onPointerDown;
      t16 = onPointerMove;
      t17 = onPointerUp;
      t18 = onPointerCancel;
      t19 = onPointerLeave;
      t20 = cn(trackClassName, "transition-colors duration-fast ease-default");
    }
    $[18] = className;
    $[19] = currentChecked;
    $[20] = disabled;
    $[21] = isControlled;
    $[22] = metrics;
    $[23] = offClass;
    $[24] = onCheckedChange;
    $[25] = onClick;
    $[26] = onPointerCancel;
    $[27] = onPointerDown;
    $[28] = onPointerLeave;
    $[29] = onPointerMove;
    $[30] = onPointerUp;
    $[31] = prefersReduced;
    $[32] = ref;
    $[33] = rest;
    $[34] = size;
    $[35] = type;
    $[36] = variant;
    $[37] = T0;
    $[38] = dataState;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
    $[51] = t4;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = thumbClassName;
  } else {
    T0 = $[37];
    dataState = $[38];
    t10 = $[39];
    t11 = $[40];
    t12 = $[41];
    t13 = $[42];
    t14 = $[43];
    t15 = $[44];
    t16 = $[45];
    t17 = $[46];
    t18 = $[47];
    t19 = $[48];
    t20 = $[49];
    t21 = $[50];
    t4 = $[51];
    t5 = $[52];
    t6 = $[53];
    t7 = $[54];
    t8 = $[55];
    t9 = $[56];
    thumbClassName = $[57];
  }
  if (t21 !== Symbol.for("react.early_return_sentinel")) {
    return t21;
  }
  let t22;
  if ($[68] !== T0 || $[69] !== dataState || $[70] !== rest || $[71] !== t10 || $[72] !== t11 || $[73] !== t12 || $[74] !== t13 || $[75] !== t14 || $[76] !== t15 || $[77] !== t16 || $[78] !== t17 || $[79] !== t18 || $[80] !== t19 || $[81] !== t20 || $[82] !== t4 || $[83] !== t5 || $[84] !== t6 || $[85] !== t7 || $[86] !== t8 || $[87] !== t9 || $[88] !== thumbClassName) {
    t22 = <T0 forwardedRef={t4} type={t5} variant={t6} size={t7} metrics={t8} currentChecked={t9} isControlled={t10} setUncontrolledChecked={t11} onCheckedChange={t12} disabled={t13} onClick={t14} onPointerDown={t15} onPointerMove={t16} onPointerUp={t17} onPointerCancel={t18} onPointerLeave={t19} trackClassName={t20} thumbClassName={thumbClassName} dataState={dataState} rest={rest} />;
    $[68] = T0;
    $[69] = dataState;
    $[70] = rest;
    $[71] = t10;
    $[72] = t11;
    $[73] = t12;
    $[74] = t13;
    $[75] = t14;
    $[76] = t15;
    $[77] = t16;
    $[78] = t17;
    $[79] = t18;
    $[80] = t19;
    $[81] = t20;
    $[82] = t4;
    $[83] = t5;
    $[84] = t6;
    $[85] = t7;
    $[86] = t8;
    $[87] = t9;
    $[88] = thumbClassName;
    $[89] = t22;
  } else {
    t22 = $[89];
  }
  return t22;
}

// ──────────────────────────────────────────────────────────────────────────
// Normal-motion sub-component — owns Motion values, refs, drag handlers
// (split out so the Motion hooks aren't called in the reduced-motion branch)
// ──────────────────────────────────────────────────────────────────────────

interface NormalMotionSwitchProps {
  forwardedRef: React.Ref<HTMLButtonElement>;
  type: 'button' | 'submit' | 'reset';
  variant: AnimatedSwitchVariant;
  size: AnimatedSwitchSize;
  metrics: SwitchMetrics;
  currentChecked: boolean;
  isControlled: boolean;
  setUncontrolledChecked: (next: boolean) => void;
  onCheckedChange: ((next: boolean) => void) | undefined;
  disabled: boolean | undefined;
  onClick: ((event: ReactMouseEvent<HTMLButtonElement>) => void) | undefined;
  onPointerDown: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined;
  onPointerMove: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined;
  onPointerUp: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined;
  onPointerCancel: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined;
  onPointerLeave: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined;
  trackClassName: string;
  thumbClassName: string;
  dataState: 'checked' | 'unchecked';
  rest: Record<string, unknown>;
}
function NormalMotionSwitch(props) {
  const $ = _c(122);
  const {
    forwardedRef,
    type,
    variant,
    size,
    metrics,
    currentChecked,
    isControlled,
    setUncontrolledChecked,
    onCheckedChange,
    disabled,
    onClick,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
    trackClassName,
    thumbClassName,
    dataState,
    rest
  } = props;
  const tokens = VARIANT_TOKENS[variant];
  const thumbTravel = metrics.thumbTravel;
  const dragStartX = useRef(0);
  const dragStartThumbX = useRef(0);
  const isDragging = useRef(false);
  const activePointerId = useRef(null);
  const suppressNextClick = useRef(false);
  const targetX = useMotionValue(currentChecked ? thumbTravel : 0);
  const thumbX = useSpring(targetX, THUMB_SPRING);
  const grabTarget = useMotionValue(0);
  const grabProgress = useSpring(grabTarget, GRAB_SPRING);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [0, 1];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = metrics.thumbX + metrics.padding * 4.5;
  let t2;
  if ($[1] !== metrics.thumbX || $[2] !== t1) {
    t2 = [metrics.thumbX, t1];
    $[1] = metrics.thumbX;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const thumbWidth = useTransform(grabProgress, t0, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 1];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = metrics.thumbY + metrics.padding * 2.3;
  let t5;
  if ($[5] !== metrics.thumbY || $[6] !== t4) {
    t5 = [metrics.thumbY, t4];
    $[5] = metrics.thumbY;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const thumbHeight = useTransform(grabProgress, t3, t5);
  let t6;
  if ($[8] !== thumbWidth || $[9] !== thumbX) {
    t6 = [thumbX, thumbWidth];
    $[8] = thumbWidth;
    $[9] = thumbX;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== metrics.thumbX) {
    t7 = latest => {
      const x = latest[0] ?? 0;
      const w = latest[1] ?? metrics.thumbX;
      return x - (w - metrics.thumbX) / 2;
    };
    $[11] = metrics.thumbX;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const thumbOffsetX = useTransform(t6, t7);
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [0, 1];
    t9 = [0, 0.76];
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  const liquidOpacity = useTransform(grabProgress, t8, t9);
  let t10;
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [0, 1];
    t11 = [0.82, 1.08];
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  const liquidScale = useTransform(grabProgress, t10, t11);
  let t12;
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [0, 1];
    t13 = [1, 0.2];
    $[17] = t12;
    $[18] = t13;
  } else {
    t12 = $[17];
    t13 = $[18];
  }
  const thumbOpacity = useTransform(grabProgress, t12, t13);
  let t14;
  if ($[19] !== thumbTravel) {
    t14 = [0, thumbTravel];
    $[19] = thumbTravel;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [0, 1];
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const activeProgress = useTransform(thumbX, t14, t15);
  let t16;
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = [0, 1];
    t17 = [0, 1];
    $[22] = t16;
    $[23] = t17;
  } else {
    t16 = $[22];
    t17 = $[23];
  }
  const fillOpacity = useTransform(activeProgress, t16, t17);
  let t18;
  let t19;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [0, 0.7, 1];
    t19 = [0, 0.18, 0.2];
    $[24] = t18;
    $[25] = t19;
  } else {
    t18 = $[24];
    t19 = $[25];
  }
  const glowOpacity = useTransform(activeProgress, t18, t19);
  let t20;
  let t21;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = [0, 1];
    t21 = [0.82, 1];
    $[26] = t20;
    $[27] = t21;
  } else {
    t20 = $[26];
    t21 = $[27];
  }
  const glowScale = useTransform(activeProgress, t20, t21);
  let t22;
  if ($[28] !== currentChecked || $[29] !== isControlled || $[30] !== onCheckedChange || $[31] !== setUncontrolledChecked || $[32] !== targetX || $[33] !== thumbTravel) {
    t22 = function setChecked(next) {
      if (next === currentChecked) {
        targetX.set(next ? thumbTravel : 0);
        return;
      }
      if (!isControlled) {
        setUncontrolledChecked(next);
      }
      targetX.set(next ? thumbTravel : 0);
      onCheckedChange?.(next);
    };
    $[28] = currentChecked;
    $[29] = isControlled;
    $[30] = onCheckedChange;
    $[31] = setUncontrolledChecked;
    $[32] = targetX;
    $[33] = thumbTravel;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  const setChecked = t22;
  let t23;
  if ($[35] !== disabled || $[36] !== grabTarget || $[37] !== onPointerDown || $[38] !== targetX || $[39] !== thumbX) {
    t23 = function handlePointerDown(event) {
      onPointerDown?.(event);
      if (event.defaultPrevented || disabled === true) {
        return;
      }
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      activePointerId.current = event.pointerId;
      grabTarget.set(1);
      dragStartX.current = event.clientX;
      dragStartThumbX.current = thumbX.get();
      targetX.set(dragStartThumbX.current);
      isDragging.current = false;
    };
    $[35] = disabled;
    $[36] = grabTarget;
    $[37] = onPointerDown;
    $[38] = targetX;
    $[39] = thumbX;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  const handlePointerDown = t23;
  let t24;
  if ($[41] !== disabled || $[42] !== onPointerMove || $[43] !== targetX || $[44] !== thumbTravel) {
    t24 = function handlePointerMove(event_0) {
      onPointerMove?.(event_0);
      if (event_0.defaultPrevented || disabled === true) {
        return;
      }
      if (activePointerId.current !== null && event_0.pointerId !== activePointerId.current) {
        return;
      }
      if (!event_0.currentTarget.hasPointerCapture(event_0.pointerId)) {
        return;
      }
      const deltaX = event_0.clientX - dragStartX.current;
      if (Math.abs(deltaX) > 3) {
        isDragging.current = true;
      }
      if (!isDragging.current) {
        return;
      }
      event_0.preventDefault();
      const nextX = dragStartThumbX.current + deltaX;
      targetX.set(clamp(nextX, 0, thumbTravel));
    };
    $[41] = disabled;
    $[42] = onPointerMove;
    $[43] = targetX;
    $[44] = thumbTravel;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  const handlePointerMove = t24;
  let t25;
  if ($[46] !== grabTarget || $[47] !== onPointerUp || $[48] !== setChecked || $[49] !== targetX || $[50] !== thumbTravel) {
    t25 = function handlePointerUp(event_1) {
      onPointerUp?.(event_1);
      if (activePointerId.current !== null && event_1.pointerId !== activePointerId.current) {
        return;
      }
      if (event_1.currentTarget.hasPointerCapture(event_1.pointerId)) {
        event_1.currentTarget.releasePointerCapture(event_1.pointerId);
      }
      activePointerId.current = null;
      grabTarget.set(0);
      if (!isDragging.current) {
        return;
      }
      isDragging.current = false;
      suppressNextClick.current = true;
      setChecked(targetX.get() >= thumbTravel / 2);
    };
    $[46] = grabTarget;
    $[47] = onPointerUp;
    $[48] = setChecked;
    $[49] = targetX;
    $[50] = thumbTravel;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const handlePointerUp = t25;
  let t26;
  if ($[52] !== currentChecked || $[53] !== grabTarget || $[54] !== onPointerCancel || $[55] !== targetX || $[56] !== thumbTravel) {
    t26 = function handlePointerCancel(event_2) {
      onPointerCancel?.(event_2);
      activePointerId.current = null;
      isDragging.current = false;
      grabTarget.set(0);
      targetX.set(currentChecked ? thumbTravel : 0);
    };
    $[52] = currentChecked;
    $[53] = grabTarget;
    $[54] = onPointerCancel;
    $[55] = targetX;
    $[56] = thumbTravel;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  const handlePointerCancel = t26;
  let t27;
  if ($[58] !== currentChecked || $[59] !== disabled || $[60] !== onClick || $[61] !== setChecked) {
    t27 = function handleClick(event_3) {
      onClick?.(event_3);
      if (event_3.defaultPrevented || disabled === true) {
        return;
      }
      if (suppressNextClick.current) {
        suppressNextClick.current = false;
        event_3.preventDefault();
        return;
      }
      setChecked(!currentChecked);
    };
    $[58] = currentChecked;
    $[59] = disabled;
    $[60] = onClick;
    $[61] = setChecked;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  const handleClick = t27;
  let t28;
  let t29;
  if ($[63] !== currentChecked || $[64] !== targetX || $[65] !== thumbTravel) {
    t28 = () => {
      if (activePointerId.current !== null) {
        return;
      }
      targetX.set(currentChecked ? thumbTravel : 0);
    };
    t29 = [currentChecked, thumbTravel, targetX];
    $[63] = currentChecked;
    $[64] = targetX;
    $[65] = thumbTravel;
    $[66] = t28;
    $[67] = t29;
  } else {
    t28 = $[66];
    t29 = $[67];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[68] !== currentChecked || $[69] !== grabTarget || $[70] !== targetX || $[71] !== thumbTravel) {
    t30 = () => {
      const cancelFromWindow = function cancelFromWindow() {
        if (activePointerId.current === null && !isDragging.current) {
          return;
        }
        isDragging.current = false;
        activePointerId.current = null;
        grabTarget.set(0);
        targetX.set(currentChecked ? thumbTravel : 0);
      };
      window.addEventListener("pointercancel", cancelFromWindow);
      window.addEventListener("blur", cancelFromWindow);
      return () => {
        window.removeEventListener("pointercancel", cancelFromWindow);
        window.removeEventListener("blur", cancelFromWindow);
      };
    };
    t31 = [thumbTravel, currentChecked, grabTarget, targetX];
    $[68] = currentChecked;
    $[69] = grabTarget;
    $[70] = targetX;
    $[71] = thumbTravel;
    $[72] = t30;
    $[73] = t31;
  } else {
    t30 = $[72];
    t31 = $[73];
  }
  useEffect(t30, t31);
  let t32;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      touchAction: "pan-y"
    };
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== glowOpacity || $[76] !== glowScale || $[77] !== tokens.glowCss) {
    t33 = <m.span aria-hidden={true} className="pointer-events-none absolute -inset-1 rounded-full blur-md" style={{
      backgroundColor: tokens.glowCss,
      opacity: glowOpacity,
      scale: glowScale
    }} />;
    $[75] = glowOpacity;
    $[76] = glowScale;
    $[77] = tokens.glowCss;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span aria-hidden={true} className="absolute inset-0 rounded-full" style={{
      boxShadow: TRACK_INSET_SHADOW
    }} />;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== fillOpacity || $[81] !== tokens.onCss) {
    t35 = <span aria-hidden={true} className="absolute inset-0 overflow-hidden rounded-full">{t34}<m.span aria-hidden={true} className="absolute inset-0 rounded-full" style={{
        backgroundColor: tokens.onCss,
        opacity: fillOpacity
      }} /></span>;
    $[80] = fillOpacity;
    $[81] = tokens.onCss;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  let t36;
  if ($[83] !== liquidOpacity || $[84] !== liquidScale || $[85] !== metrics.padding || $[86] !== thumbHeight || $[87] !== thumbOffsetX || $[88] !== thumbWidth) {
    t36 = <m.span aria-hidden={true} className="pointer-events-none absolute left-0 top-1/2 z-1 block rounded-full" style={{
      width: thumbWidth,
      height: thumbHeight,
      x: thumbOffsetX,
      y: "-50%",
      marginLeft: metrics.padding,
      background: "color-mix(in srgb, var(--background-primary) 82%, transparent)",
      opacity: liquidOpacity,
      scale: liquidScale,
      filter: "blur(9px)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)"
    }} />;
    $[83] = liquidOpacity;
    $[84] = liquidScale;
    $[85] = metrics.padding;
    $[86] = thumbHeight;
    $[87] = thumbOffsetX;
    $[88] = thumbWidth;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== thumbClassName) {
    t37 = cn(thumbClassName, "pointer-events-none absolute z-2");
    $[90] = thumbClassName;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== metrics.padding || $[93] !== thumbHeight || $[94] !== thumbOffsetX || $[95] !== thumbOpacity || $[96] !== thumbWidth) {
    t38 = {
      width: thumbWidth,
      height: thumbHeight,
      left: metrics.padding,
      top: "50%",
      x: thumbOffsetX,
      y: "-50%",
      opacity: thumbOpacity,
      boxShadow: THUMB_GLASS_SHADOW
    };
    $[92] = metrics.padding;
    $[93] = thumbHeight;
    $[94] = thumbOffsetX;
    $[95] = thumbOpacity;
    $[96] = thumbWidth;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== dataState || $[99] !== t37 || $[100] !== t38) {
    t39 = <m.span aria-hidden={true} data-slot="switch-thumb" data-state={dataState} className={t37} style={t38} />;
    $[98] = dataState;
    $[99] = t37;
    $[100] = t38;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== currentChecked || $[103] !== dataState || $[104] !== disabled || $[105] !== forwardedRef || $[106] !== handleClick || $[107] !== handlePointerCancel || $[108] !== handlePointerDown || $[109] !== handlePointerMove || $[110] !== handlePointerUp || $[111] !== onPointerLeave || $[112] !== rest || $[113] !== size || $[114] !== t33 || $[115] !== t35 || $[116] !== t36 || $[117] !== t39 || $[118] !== trackClassName || $[119] !== type || $[120] !== variant) {
    t40 = <LazyMotion features={domAnimation}><m.button ref={forwardedRef} type={type} role="switch" aria-checked={currentChecked} data-slot="switch" data-state={dataState} data-variant={variant} data-size={size} disabled={disabled} className={trackClassName} style={t32} onClick={handleClick} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerCancel} onPointerLeave={onPointerLeave} {...rest}>{t33}{t35}{t36}{t39}</m.button></LazyMotion>;
    $[102] = currentChecked;
    $[103] = dataState;
    $[104] = disabled;
    $[105] = forwardedRef;
    $[106] = handleClick;
    $[107] = handlePointerCancel;
    $[108] = handlePointerDown;
    $[109] = handlePointerMove;
    $[110] = handlePointerUp;
    $[111] = onPointerLeave;
    $[112] = rest;
    $[113] = size;
    $[114] = t33;
    $[115] = t35;
    $[116] = t36;
    $[117] = t39;
    $[118] = trackClassName;
    $[119] = type;
    $[120] = variant;
    $[121] = t40;
  } else {
    t40 = $[121];
  }
  return t40;
}